const SEARCH_OPTS = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
};

function norm(value) {
  return String(value)
    .toLowerCase()
    .replace(/[+/.\-,&]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Damerau-Levenshtein dist-1 typo tolerance applies only to purely alphabetic
// terms of 4+ chars; digit-bearing or short terms must match exactly so
// near-duplicate codes like PC380 / PC381 are never conflated (F13/DECIDE-10).
function isFuzzyTerm(term) {
  return term.length >= 4 && /^[a-z]+$/.test(term);
}

class SanmarClearance extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.suiteletUrl;
    const status = this.querySelector(".sanmar-clearance__status");
    const toolbar = this.querySelector(".sanmar-clearance__toolbar");

    this.table = this.querySelector(".sanmar-clearance__table");
    this.tbody = this.table.querySelector("tbody");
    this.brandPanel = this.querySelector(".sanmar-clearance__brand-panel");
    this.searchInput = this.querySelector(".sanmar-clearance__search");
    this.searchClear = this.querySelector(".sanmar-clearance__search-clear");
    this.noMatch = this.querySelector(".sanmar-clearance__no-match");
    this.announce = this.querySelector(".sanmar-clearance__announce");

    const errorMessage =
      "List temporarily unavailable. Please try again later.";

    fetch(url, { method: "GET" })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.error) {
          this.setStatus(status, "error", errorMessage);
          return;
        }
        if (!data || !Array.isArray(data.items) || data.items.length === 0) {
          this.setStatus(status, "empty", "No items currently listed.");
          return;
        }

        this.items = data.items;
        // Brand is part of the search haystack (designer spec §2 + the V2
        // placeholder; folded into F13 2026-07-03), so brand terms get the
        // same gated typo tolerance as name terms.
        this.haystacks = this.items.map((item) =>
          norm(
            (item.brand || "") +
              " " +
              (item.name || "") +
              " " +
              (item.sku || ""),
          ),
        );
        this.populateBrands(data.items);
        this.renderRows(data.items);

        status.hidden = true;
        this.table.hidden = false;
        toolbar.hidden = false;

        this.brandPanel.addEventListener("change", () => this.applyFilter());
        // Clicking an option row's label text/padding (not the checkbox itself)
        // shifts focus off the <summary>; DetailsDisclosure.onFocusOut then
        // closes the panel before the click toggles the box. Preventing the
        // mousedown default keeps focus put — the click still toggles the
        // checkbox and fires the Clear button, which activate on click (B-2).
        this.brandPanel.addEventListener("mousedown", (event) =>
          event.preventDefault(),
        );
        this.searchInput.addEventListener("input", () => this.applyFilter());
        this.searchClear.addEventListener("click", () => this.clearSearch());
      })
      .catch(() => {
        this.setStatus(status, "error", errorMessage);
      });
  }

  populateBrands(items) {
    const brands = [
      ...new Set(items.map((item) => item.brand).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b));
    for (const brand of brands) {
      const option = document.createElement("label");
      option.className = "sanmar-clearance__brand-option";

      const text = document.createElement("span");
      text.textContent = brand;

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.value = brand;

      option.append(text, checkbox);
      this.brandPanel.append(option);
    }

    const clearWrap = document.createElement("div");
    clearWrap.className = "sanmar-clearance__brand-clear";
    const clearButton = document.createElement("button");
    clearButton.type = "button";
    clearButton.textContent = "Clear Filter";
    clearButton.addEventListener("click", () => this.clearBrands());
    clearWrap.append(clearButton);
    this.brandPanel.append(clearWrap);
  }

  clearBrands() {
    for (const checkbox of this.brandPanel.querySelectorAll("input")) {
      checkbox.checked = false;
    }
    this.applyFilter();
  }

  clearSearch() {
    this.searchInput.value = "";
    this.searchInput.focus();
    this.applyFilter();
  }

  applyFilter() {
    const selectedBrands = Array.from(
      this.brandPanel.querySelectorAll("input:checked"),
    ).map((checkbox) => checkbox.value);
    const brandMatch = (item) =>
      selectedBrands.length === 0 || selectedBrands.includes(item.brand);
    const query = norm(this.searchInput.value);

    let matches;
    if (query === "") {
      matches = this.items.filter(brandMatch);
    } else {
      const candidates = [];
      const haystacks = [];
      this.items.forEach((item, index) => {
        if (brandMatch(item)) {
          candidates.push(item);
          haystacks.push(this.haystacks[index]);
        }
      });
      matches = this.searchRank(candidates, haystacks, query);
    }

    this.renderRows(matches);

    // Keep the table (and its <thead>, which now hosts the brand-filter
    // dropdown) visible across filter states; only the body empties and the
    // no-match notice toggles (F15 "in place of the table body").
    if (matches.length === 0) {
      this.noMatch.hidden = false;
      this.announce.textContent = "No items match your search.";
    } else {
      this.noMatch.hidden = true;
      this.announce.textContent =
        matches.length === 1
          ? "1 item shown."
          : matches.length + " items shown.";
    }
  }

  // Out-of-order multi-term AND + in-token substring + relevance ranking are
  // delegated to uFuzzy; SingleError mode fuzzes every term, so the exact-term
  // gate below tightens digit/short terms back to exact substring (DECIDE-10/11).
  searchRank(candidates, haystacks, query) {
    if (candidates.length === 0) {
      return [];
    }
    this.uf = this.uf || new uFuzzy(SEARCH_OPTS);
    const [idxs, info, order] = this.uf.search(haystacks, query, true);
    if (!idxs || idxs.length === 0) {
      return [];
    }
    const exactTerms = query.split(" ").filter((term) => !isFuzzyTerm(term));
    const ranked = [];
    for (const position of order) {
      const index = info.idx[position];
      const haystack = haystacks[index];
      if (exactTerms.every((term) => haystack.includes(term))) {
        ranked.push(candidates[index]);
      }
    }
    return ranked;
  }

  renderRows(items) {
    this.tbody.replaceChildren(...items.map((item) => this.buildRow(item)));
  }

  buildRow(item) {
    const row = document.createElement("tr");

    const brandCell = document.createElement("td");
    brandCell.textContent = item.brand;

    const nameCell = document.createElement("td");
    nameCell.textContent = item.name;

    const skuCell = document.createElement("td");
    skuCell.textContent = item.sku;

    const qtyCell = document.createElement("td");
    qtyCell.className = "sanmar-clearance__cell--numeric";
    qtyCell.textContent = item.available;

    row.append(brandCell, nameCell, skuCell, qtyCell);
    return row;
  }

  setStatus(status, state, message) {
    status.dataset.state = state;
    status.textContent = message;
  }
}

customElements.define("sanmar-clearance", SanmarClearance);
